import type { Applicant, ApplicantPhotoType, BranchCode, CountryKey, Domain, Order, PaymentProcessor, VisaForm } from "~/types";
import type { NitroFetchRequest, NitroConfig } from "nitropack";
import { getDomainByBranch, toProductionDomain } from "./domain";
import { getVisaCountryPath, getVisaCountryPaths } from './visa-country';
import { countryKeyToSlug } from "./format";
import { omitFalsyProps, objectEntries } from "./general";
import { RESUME_TOKEN_QUERY_KEY } from "../assets/constants";
import { getBranchByCode } from "./branch";
import LEGACY_WP_BLOG_SLUGS from '../assets/legacy-wp-blog-slugs.json';

type UtmParams = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
};

const BLOG_PATH = '/blog';
const PAYMENT_HANDLER_ROUTE = '/api/payment-handler';
const PRERENDERED_ROUTES = [
  '/', '/contact', '/about', '/privacy', '/terms', '/status',
  ...getVisaCountryPaths()
];
const REDIRECTS: Record<string, string> = {
  '/reviews': '/',
  '/india/e-visa': '/india',
  '/vietnam/urgent-visa': '/vietnam',
  '/блог-новости': BLOG_PATH,
  '/виза-в-индию-доступна-для-туризма-2021': BLOG_PATH,
  '/usa': '/us',
  ...getLegacyWpRedirects()
};
const SECTION_IDS = {
  'countries_overview': 'overview'
};
const CAPTCHA_ROUTES: Readonly<NitroFetchRequest[]> = [
  '/api/save-form',
  '/api/upload-photo',
  '/api/send-contact-message',
  '/api/payment-intent',
  '/api/create-paypal-order',
  '/api/send-resume-url',
  '/api/order-status'
];

export function buildUrl(domain: Domain, path: string) {
  return join(buildHostUrl(domain), path);
}

export function buildHostUrl(domain: Domain) {
  return `${getProtocol(domain)}://${domain}`;
}

export function buildHostUrlByBranch(branch: BranchCode) {
  const domain = getDomainByBranch(branch);
  return buildHostUrl(domain);
}

export function buildVisaCountryPageUrl(domain: Domain, country: CountryKey) {
  const path = getVisaCountryPath(country);
  return buildUrl(domain, path);
}

export function buildSuccessRoute(formId: VisaForm['id'], country: CountryKey, processor: PaymentProcessor, additionals: Record<string, string>) {
  return "success?" + buildUrlQuery({
    form_id: formId,
    country_key: country,
    payment_processor: processor,
    ...omitFalsyProps(additionals)
  });
}

export function buildWhatsappLink(phoneRaw: string) {
  return join('https://wa.me', phoneRaw);
}

export function isAuthRoute(route: NitroFetchRequest) {
  return (route as string).startsWith('/api/admin/');
}

export function isCaptchaRoute(route: NitroFetchRequest) {
  return CAPTCHA_ROUTES.includes(route);
}

export function isCronRoute(route: NitroFetchRequest) {
  return (route as string).startsWith('/api/cron/');
}

export function isApiRoute(route: NitroFetchRequest) {
  return (route as string).startsWith('/api/');
}

export function stripUrlQuery(url: string) {
  return url.split('?')[0];
}

export function isAbsolutePath(url: string) {
  return url.includes('http');
}

export function buildPhotoUploadPath(formId: VisaForm['id'], applicantId: Applicant['id'], type: ApplicantPhotoType) {
  return join('photos', formId, `${applicantId}-${type}`); // photos/pNcMmZjKxdQigkLFLwQk/israel_cohen-face
}

export function buildVisaDocUploadPath(id: Order['id'], visaNum: string) {
  const folder = buildVisaFolderPath(id);
  return join(folder, visaNum);
}

export function buildVisaFolderPath(id: Order['id']) {
  return join('visas', id) + '/';
}

export function buildInvoiceUploadPath(orderId: Order['id'], invoiceNumber: string) {
  return join('invoices', orderId, `${invoiceNumber}.pdf`);
}

export function buildBranchEmailAddress(branch: BranchCode) {
  const { emailUsername } = getBranchByCode(branch);
  const domain = getDomainByBranch(branch);
  return buildEmailAddress(emailUsername, domain);
}

export function buildPhotosEmailAddress(domain: Domain) {
  return buildEmailAddress('photos', domain);
}

export function isBlogRoute(path: string) {
  return path.includes(BLOG_PATH);
}

export function isBlogPostRoute(path: string) {
  return new RegExp(`^${BLOG_PATH}/.+`).test(path);
}

export function buildArticleUrl(domain: Domain, slug: string) {
  const path = buildArticlePath(slug);
  return buildUrl(domain, path);
}

export function buildArticlePath(slug: string) {
  return join(BLOG_PATH, slug);
}

export function buildBlogCategoryPath(slug: string) {
  return join(BLOG_PATH, 'category', slug);
}

export function buildAuthorPath(author: string) {
  return join(BLOG_PATH, 'author', author);
}

export function buildPathAncestry(path: string) {
  const paths = [''];
  path.split('/').filter(Boolean).forEach((segment, i) => {
    paths.push(`${paths[i]}/${segment}`);
  });
  return paths;
}

export function getLastInPath(path: string) {
  return path.split('/').filter(Boolean).pop();
}

export function stripTrailingSlash(url: string) {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export function buildUrlQuery(obj: Record<string, string>) {
  return objectEntries(obj).map(pair => pair.map(encodeURIComponent).join('=')).join('&');
}

export function buildResumeFormPath(country: CountryKey, token: string) {
  const slug = countryKeyToSlug(country);
  return `${slug}?${RESUME_TOKEN_QUERY_KEY}=${token}`;
}

export function buildPrerenderRouteRules() {
  const rules: NitroConfig['routeRules'] = {};
  for (const route of PRERENDERED_ROUTES) {
    rules[route] = { prerender: true };
  }
  return rules;
}

export function getPaymentHandlerRoute() {
  return PAYMENT_HANDLER_ROUTE;
}

export function buildRedirectRules() {
  const rules: NitroConfig['routeRules'] = {};
  for (const from in REDIRECTS) {
    const encodedUrl = encodeURI(from);
    rules[encodedUrl] = {
      redirect: { statusCode: 301, to: REDIRECTS[from] }
    };
  }
  return rules;
}

export function appendUtmQuery(path: string, utm: UtmParams) {
  return path + getQueryConcatinator(path) + buildUrlQuery(utm);
}

export function getSectionId(key: keyof typeof SECTION_IDS) {
  return SECTION_IDS[key];
}

export function getHashId(key: keyof typeof SECTION_IDS) {
  return `#${getSectionId(key)}`;
}

export function getProtocol(domain: Domain) {
  return isLocalhost(domain) ? 'http' : 'https';
}

export function isLocalhost(host: string) {
  return host.includes('localhost');
}

function getLegacyWpRedirects() {
  return LEGACY_WP_BLOG_SLUGS.reduce<Record<string, string>>((acc, slug) => {
    acc[`/${slug}`] = buildArticlePath(encodeURIComponent(slug));
    return acc;
  }, {});
}

function getQueryConcatinator(path: string) {
  return path.includes('?') ? '&' : '?';
}

function join(...parts: string[]) {
  return parts.join('/').replace(/[\/]+/g, '/').replace(/^(.+):\//, '$1://')
    .replace(/\/(\?|&|#[^!])/g, '$1').replace(/\?/g, '&').replace('&', '?');
}

function buildEmailAddress(name: string, domain: Domain) {
  return `${name}@${toProductionDomain(domain)}`;
}